import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { logAdminAction } from '../../utils/auditLogger';
import * as LucideIcons from 'lucide-react';

export default function DiscountCategoriesManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    icon: 'Tag', // alapértelmezett ikon
    isActive: true
  });

  const AVAILABLE_ICONS = [
    'Tag', 'Tags', 'Ticket', 'Percent', 'Gift', 'Package', 'ShoppingBag', 
    'ShoppingCart', 'Store', 'CreditCard', 'Wallet', 'DollarSign', 
    'Star', 'Heart', 'Award', 'Medal'
  ];

  const fetchCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'discountCategories'));
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Hiba történt a kategóriák betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const docRef = await addDoc(collection(db, 'discountCategories'), {
        ...newCategory,
        created_at: new Date()
      });

      await logAdminAction('discount_category.create', {
        categoryId: docRef.id,
        name: newCategory.name
      });

      toast.success('Kategória sikeresen létrehozva');
      setNewCategory({ name: '', description: '', icon: 'Tag', isActive: true });
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Hiba történt a kategória létrehozásakor');
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    try {
      await updateDoc(doc(db, 'discountCategories', categoryId), {
        ...editingCategory,
        last_updated: new Date()
      });

      await logAdminAction('discount_category.update', {
        categoryId,
        name: editingCategory.name
      });

      toast.success('Kategória sikeresen módosítva');
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Hiba történt a kategória módosításakor');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a kategóriát?')) return;

    try {
      await deleteDoc(doc(db, 'discountCategories', categoryId));
      
      await logAdminAction('discount_category.delete', {
        categoryId
      });

      toast.success('Kategória sikeresen törölve');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Hiba történt a kategória törlésekor');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Kedvezmény Kategóriák</h1>
        <p className="mt-1 text-sm text-gray-500">Kedvezmény kategóriák kezelése</p>
      </div>

      {/* Új kategória form */}
      <div className="bg-white rounded-lg shadow p-10 mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8">Új Kategória Létrehozása</h2>
        <form onSubmit={handleCreateCategory} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-base font-medium text-gray-700 mb-3">
                Kategória neve
              </label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-3">
                Ikon
              </label>
              <div className="relative">
                <select
                  value={newCategory.icon}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                >
                  {AVAILABLE_ICONS.map(iconName => (
                    <option key={iconName} value={iconName}>
                      {iconName}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none">
                  {LucideIcons[newCategory.icon] && React.createElement(LucideIcons[newCategory.icon], {
                    className: "h-6 w-6 text-indigo-500"
                  })}
                </div>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-3">
              Leírás
            </label>
            <textarea
              value={newCategory.description}
              onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
              rows={6}
              className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="flex items-center bg-gray-50 p-6 rounded-lg">
            <input
              type="checkbox"
              checked={newCategory.isActive}
              onChange={(e) => setNewCategory(prev => ({ ...prev, isActive: e.target.checked }))}
              className="h-6 w-6 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label className="ml-3 text-lg text-gray-700">Aktív</label>
            <div className="ml-auto">
              <span className={`px-4 py-2 text-base font-medium rounded-full ${
                newCategory.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {newCategory.isActive ? 'Aktív' : 'Inaktív'}
              </span>
            </div>
          </div>
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="px-8 py-4 bg-indigo-600 text-white text-base font-medium rounded-lg hover:bg-indigo-700 flex items-center"
            >
              <Icons.Plus className="h-6 w-6 mr-2" />
              <span>Kategória Létrehozása</span>
            </button>
          </div>
        </form>
      </div>

      {/* Kategóriák listája */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Név</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leírás</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Státusz</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map(category => (
              <tr key={category.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-indigo-50 rounded-full flex items-center justify-center">
                      {LucideIcons[category.icon] && React.createElement(LucideIcons[category.icon], {
                        className: "h-6 w-6 text-indigo-500"
                      })}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{category.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500 max-w-md">{category.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1.5 inline-flex text-sm font-medium rounded-full ${
                    category.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {category.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => setEditingCategory(category)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4 inline-flex items-center"
                  >
                    <Icons.Edit2 className="h-5 w-5 mr-1" />
                    <span>Szerkesztés</span>
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-600 hover:text-red-900 inline-flex items-center"
                  >
                    <Icons.Trash2 className="h-5 w-5 mr-1" />
                    <span>Törlés</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Szerkesztés Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-10 rounded-lg shadow-xl max-w-7xl w-full">
            <div className="flex justify-between items-start mb-8">
              <h2 className="text-2xl font-semibold text-gray-900">Kategória Szerkesztése</h2>
              <button
                onClick={() => setEditingCategory(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <Icons.X className="h-7 w-7" />
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateCategory(editingCategory.id);
            }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-3">
                    Kategória neve
                  </label>
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-3">
                    Ikon
                  </label>
                  <div className="relative">
                    <select
                      value={editingCategory.icon}
                      onChange={(e) => setEditingCategory(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                    >
                      {AVAILABLE_ICONS.map(iconName => (
                        <option key={iconName} value={iconName}>
                          {iconName}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none">
                      {LucideIcons[editingCategory.icon] && React.createElement(LucideIcons[editingCategory.icon], {
                        className: "h-6 w-6 text-indigo-500"
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-3">
                  Leírás
                </label>
                <textarea
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, description: e.target.value }))}
                  rows={6}
                  className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center bg-gray-50 p-6 rounded-lg">
                <input
                  type="checkbox"
                  checked={editingCategory.isActive}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-6 w-6 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label className="ml-3 text-lg text-gray-700">Aktív</label>
                <div className="ml-auto">
                  <span className={`px-4 py-2 inline-flex text-base font-medium rounded-full ${
                    editingCategory.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {editingCategory.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditingCategory(null)}
                  className="px-8 py-4 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-8 py-4 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700"
                >
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 